import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AITutor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "你好 (Nǐ hǎo)! I'm Lǎoshī 老师, your AI Chinese tutor. Ask me anything — grammar, pinyin, HSK vocabulary, or practice a conversation!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (!user) { setOpen(false); navigate("/login"); return; }
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/tutor/chat", { message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="tutor-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-cinnabar text-rice flex items-center justify-center shadow-lg hover:bg-ink transition-colors duration-300 border-2 border-ink">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div data-testid="tutor-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[520px] bg-white border-2 border-ink flex flex-col shadow-2xl animate-fade-up">
          <div className="bg-cinnabar text-rice px-4 py-3 flex items-center gap-3 border-b-2 border-ink">
            <span className="w-9 h-9 bg-rice text-cinnabar flex items-center justify-center font-serif text-xl">师</span>
            <div className="leading-tight">
              <p className="font-display font-bold text-sm">Lǎoshī · AI Tutor</p>
              <p className="text-[10px] text-rice/70">Mandarin practice, anytime</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-rice">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-ink text-rice" : "bg-white border border-black/10 text-ink"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-clay font-mono">老师 is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t-2 border-ink bg-white flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={user ? "Type in English or 中文…" : "Log in to chat"} data-testid="tutor-input"
              className="flex-1 px-3 py-2 text-sm border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar bg-white" />
            <button onClick={send} data-testid="tutor-send" disabled={busy}
              className="px-3 bg-cinnabar text-rice hover:bg-ink transition-colors disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
